import styles from './MeritoSelectField.module.scss';
import Select, { components } from 'react-select';
import { FaSearch } from 'react-icons/fa'; // Search icon
import { FaChevronDown } from 'react-icons/fa'; // Chevron down icon
import { ChangeEventHandler } from 'react';

type Props = {
  options: any[],
  label: string,
  optionLabelKey: string,
  isSearchable?: boolean,
  isClearable?: boolean,
  error: any,
  touched: boolean,
  showError?: boolean;
  value: any;
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
  onClear?: () => void,
  placeholder?: string,
  noOptionsMessage?: string,
  menuPortalTarget?: HTMLElement | null,
  size?: 'sm' | 'sm2' | 'md' | 'lg' | 'signUpSm'
}

const fontStyles = {
  'sm': {
    fontFamily: "Work Sans",
    fontSize: "0.6875rem",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "0.875rem", /* 127.273% */
    letterSpacing: "0.00688rem",
  },
  'sm2': {
    fontFamily: "Work Sans",
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "1rem",
    letterSpacing: "0.00688rem",
  },
  'md': {
    fontFamily: "Work Sans",
    fontSize: "0.875rem",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "1.125rem", /* 128.571% */
    letterSpacing: "0.00875rem",
  },
  'lg': {
    fontFamily: "Work Sans",
    fontSize: "0.875rem",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "1.125rem", /* 128.571% */
    letterSpacing: "0.00875rem",
  },
  'mdMobile': {
    fontFamily: "Work Sans",
    fontSize: "0.6125rem",
    fontStyle: "normal",
    fontWeight: "500",
    lineHeight: "1.125rem",
    letterSpacing: "0.00813rem",
  },
  'signUpSm': {
    color: "#d4d4d4",
    fontFamily: "Roboto",
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "1.125rem"
  },
  'signUpSmOptions': {
    color: "black",
    fontFamily: "Roboto",
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "1.125rem"
  }
}

// Helper function to get responsive font styles
const getResponsiveFontStyles = (size: string) => {
  if (size === 'md' && window.innerWidth <= 768) {
    return fontStyles['mdMobile'];
  }
  return fontStyles[size] || fontStyles['md'];
};
const heightStyles = {
  'sm': {
    height: "36px",
    minHeight: "36px"
  },
  'sm2': {
    height: "32px",
    minHeight: "32px",
  },
  'md': {
    height: "40px",
    // height: "20px"
  },
  'lg': {
    height: "48px",
    minHeight: "48px"
  },
  'signUpSm': {
    height: "20px",
  }
}

const valueContainerStyles = {
  'sm2': {
    padding: "0 0.5rem",
    height: "100%"
    // height: "28px",
    // minHeight: "28px",
  }
}

const dropdownIndicatorStyles = {
  'sm2': {
    padding: "0rem 0.5rem"
  }
}

const customStyles = (error, touched, size) => ({
  control: (provided, state) => ({
    ...provided,
    borderRadius: "0.5rem",
    // margin: 8,
    border: error && touched ? "1px solid #E94F4F" : size == "signUpSm" ? "1px solid #c5c5c5" : "1px solid #d4d4d4",
    outline: 0,
    // padding: "0.15rem 0.3rem",
    // height: "40px",
    display: "flex",
    alignItems: "center",
    ...heightStyles[size],
    boxShadow: "none",
    // fontFamily: "Work Sans",
    // fontSize: "0.875rem",
    // fontStyle: "normal",
    // fontWeight: "500",
    // lineHeight: "1.125rem", /* 128.571% */
    // letterSpacing: "0.00875rem",
    backgroundColor: size == "signUpSm" ? "#fff" : "#F9F9F9",
    ...getResponsiveFontStyles(size),

    ":hover": {
      cursor: 'pointer',
      border: error && touched ? "1px solid #E94F4F" : "1px solid #d4d4d4"
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#fff",
    borderRadius: "0.5rem",
  }),
  menuList: (base) => ({
    ...base,
    "::-webkit-scrollbar": {
      width: "5px",
      height: "0px",
    },
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      color: "#212121",
      backgroundColor: isFocused ? "#F0EFF4" : "transparent",
      // "fontFamily": "Work Sans",
      // "fontSize": "0.875rem",
      // "fontStyle": "normal",
      // "fontWeight": "500",
      // "lineHeight": "1.125rem", /* 128.571% */
      // "letterSpacing": "0.00875rem",
      ...getResponsiveFontStyles(size == 'signUpSm' ? 'signUpSmOptions' : size),

      '&:hover': {
        cursor: "pointer"
      },

      ':active': {
        backgroundColor: "#F0EFF4",
      }
    }
  },
  noOptionsMessage: (styles) => {
    return {
      ...styles,
      color: "#56595C",
      // "fontFamily": "Work Sans",
      // "fontSize": "0.875rem",
      // "fontStyle": "normal",
      // "fontWeight": "500",
      // "lineHeight": "1.125rem", /* 128.571% */
      // "letterSpacing": "0.00875rem",
      ...getResponsiveFontStyles(size),
    }
  },
  placeholder: (styles) => ({
    ...styles,
    color: "#aaaaaa",
    // "fontFamily": "Work Sans",
    // "fontSize": "0.875rem",
    // "fontStyle": "normal",
    // "fontWeight": "400",
    // "lineHeight": "1.125rem", /* 128.571% */
    // "letterSpacing": "0.00875rem",
    ...getResponsiveFontStyles(size),
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  // Colour of the selected value
  singleValue: () => ({
    color: '#212121',
    gridArea: '1 / 1 / 2 / 3',
  }),
  valueContainer: (provided) => ({
    ...provided,
    ...valueContainerStyles[size] || {},
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    ...dropdownIndicatorStyles[size] || {},
  }),
})



const MeritoSelectField = ({ options, label, onClear = () => { }, optionLabelKey, isSearchable = true, isClearable = false, error, touched, showError = true, onChange, value, placeholder = null, noOptionsMessage = "Not Found", size = 'md', menuPortalTarget = document.body, ...restProps }: Props) => {

  return (
    <div id={`${name}`} className={`${styles.selectFieldRootContainer} ${size === 'lg' ? styles.lg : size === 'md' ? styles.md : ''
      }`}>
      {label && <div className={`${styles.label} ${size == 'sm' ? styles.labelSM : ''} `}>{label}</div>}
      <div className={styles.selectFieldContainer}>
        <Select
          {...restProps}
          options={options}
          styles={customStyles(error, touched, size)}
          isClearable={isClearable}
          isSearchable={isSearchable}
          placeholder={placeholder ? placeholder : ''}
          value={value}

          onChange={(newValue, actionMeta) => {
            console.log('newValue', newValue);
            if (!newValue) {
              onClear();
            }
            onChange({ target: { value: newValue } } as any)
          }}
          getOptionLabel={(option) => option[optionLabelKey]}
          // menuPortalTarget={size == 'signUpSm' ? menuPortalTarget : document.body}
          menuPortalTarget={null}
          menuPlacement='auto'
          menuShouldBlockScroll={true}
          noOptionsMessage={() => {
            return <div>{noOptionsMessage}</div>
          }}
        />
      </div>
      {error && touched && showError ? <span className={size == 'signUpSm' ? styles.signUpErrorMessage : styles.errorMessage}>{error}</span> : null}
    </div>
  )
}

export default MeritoSelectField;